<script setup>
import { computed, ref } from 'vue';
import { useProfileStore } from '@/js/core/stores/profile-store';
import {
  TIMEZONE_OPTIONS,
  detectTimezone,
  getTimezoneLabel,
  getTimezoneOptionLabel,
  isDefaultTimezone,
  normalizeTimezone,
} from '@/js/core/utils/timezone';

const props = defineProps({
  timezone: {
    type: Object,
    default: () => ({}),
  },
});

const profileStore = useProfileStore();
const timezoneControlId = 'profile-timezone-toggle';
const timezoneDescriptionId = 'profile-timezone-description';
const timezoneSelectId = 'profile-timezone-select';

const defaultTimezone = computed(() => normalizeTimezone(props.timezone.default_timezone));
const selectedTimezone = computed(() =>
  normalizeTimezone(props.timezone.timezone, defaultTimezone.value)
);
const isTimezoneOn = computed(() => Boolean(props.timezone.use_custom_timezone));

const timezones = computed(() => {
  if (!selectedTimezone.value || TIMEZONE_OPTIONS.some(tz => tz.value === selectedTimezone.value)) {
    return TIMEZONE_OPTIONS;
  }

  return [
    {
      value: selectedTimezone.value,
      label: getTimezoneOptionLabel(selectedTimezone.value),
    },
    ...TIMEZONE_OPTIONS,
  ];
});

const selectedTimezoneLabel = computed(() => getTimezoneLabel(selectedTimezone.value));
const defaultTimezoneLabel = computed(() => getTimezoneLabel(defaultTimezone.value));
const isSavingTimezone = ref(false);
const timezoneError = ref('');
const timezoneFieldError = ref('');

const saveTimezone = async (payload, { onError } = {}) => {
  isSavingTimezone.value = true;
  timezoneError.value = '';
  timezoneFieldError.value = '';

  try {
    await profileStore.saveTimezoneSettings(payload);
  } catch (error) {
    onError?.(error);
    timezoneFieldError.value = error.errors?.use_custom_timezone || error.errors?.timezone || '';
    timezoneError.value = timezoneFieldError.value
      ? ''
      : error.message || 'Failed to save your timezone settings. Please try again.';
  } finally {
    isSavingTimezone.value = false;
  }
};

const handleTimezoneSelect = async event => {
  await saveTimezone(
    { timezone: event.target.value, use_custom_timezone: true },
    {
      onError: () => {
        event.target.value = selectedTimezone.value;
      },
    }
  );
};

const handleTimezoneToggle = async event => {
  const enabled = event.target.checked;

  // Toggling on while still on the site default means the user never picked a
  // zone, so detect theirs. Otherwise respect the saved selection.
  const timezone =
    enabled && isDefaultTimezone(selectedTimezone.value, defaultTimezone.value)
      ? detectTimezone(defaultTimezone.value)
      : selectedTimezone.value;

  await saveTimezone(
    { timezone, use_custom_timezone: enabled },
    {
      onError: () => {
        event.target.checked = isTimezoneOn.value;
      },
    }
  );
};
</script>

<template>
  <li class="profile-page_profile-grid-item timezone">
    <label class="profile-page_timezone-label" :for="timezoneSelectId">Time zone</label>
    <div class="profile-page_timezone-select-container">
      <select
        :id="timezoneSelectId"
        :value="selectedTimezone"
        class="profile-page_timezone-dropdown"
        :disabled="isSavingTimezone"
        :aria-invalid="Boolean(timezoneFieldError)"
        @change="handleTimezoneSelect"
      >
        <option v-for="tz in timezones" :key="tz.label" :value="tz.value">
          {{ tz.label }}
        </option>
      </select>
    </div>
    <label
      class="profile-page_timezone-toggle-row"
      :for="timezoneControlId"
      :aria-describedby="timezoneDescriptionId"
    >
      <span class="profile-page_timezone-toggle-label">Switch to my time zone</span>
      <span class="profile-page_timezone-toggle-control">
        <input
          :id="timezoneControlId"
          type="checkbox"
          class="profile-page_timezone-toggle-input"
          :checked="isTimezoneOn"
          :disabled="isSavingTimezone"
          :aria-invalid="Boolean(timezoneFieldError)"
          @change="handleTimezoneToggle"
        />
        <span class="profile-page_timezone-toggle-background" aria-hidden="true">
          <span
            class="profile-page_timezone-toggle-slider"
            :class="{ 'visibility-on': isTimezoneOn }"
          ></span>
        </span>
      </span>
    </label>
    <div
      v-if="!isTimezoneOn"
      :id="timezoneDescriptionId"
      class="profile-page_timezone-message denied"
    >
      All times on the site will be displayed in the default Cambridge, MA time zone ({{
        defaultTimezoneLabel
      }}).
    </div>
    <div v-else :id="timezoneDescriptionId" class="profile-page_timezone-message accepted">
      All times on the site will be displayed in your selected time zone ({{
        selectedTimezoneLabel
      }}).
    </div>
    <p class="profile-page_timezone-message">
      Time zone information is not shared with other users.
    </p>
    <p v-if="timezoneFieldError" class="profile-page_timezone-message denied" role="alert">
      {{ timezoneFieldError }}
    </p>
    <p v-if="timezoneError" class="profile-page_timezone-message denied" role="alert">
      {{ timezoneError }}
    </p>
  </li>
</template>
